import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import autopilotCampaignService from '../../services/autopilotCampaign';

function CampaignList({ onSelectCampaign, onCreateCampaign }) {
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const fetchCampaigns = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const data = await autopilotCampaignService.getCampaigns(params);
            setCampaigns(data.campaigns || []);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            toast.error('Failed to load campaigns');
        } finally {
            setIsLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]);

    const handleStartCampaign = async (id, e) => {
        e.stopPropagation();
        try {
            await autopilotCampaignService.startCampaign(id);
            toast.success('Campaign started!');
            fetchCampaigns();
        } catch (error) {
            toast.error('Failed to start campaign');
        }
    };

    const handlePauseCampaign = async (id, e) => {
        e.stopPropagation();
        try {
            await autopilotCampaignService.pauseCampaign(id);
            toast.success('Campaign paused');
            fetchCampaigns();
        } catch (error) {
            toast.error('Failed to pause campaign');
        }
    };

    const handleSourceCandidates = async (id, e) => {
        e.stopPropagation();
        try {
            const result = await autopilotCampaignService.sourceCandidates(id);
            toast.success(`Found ${result.candidatesFound} candidates!`);
            fetchCampaigns();
        } catch (error) {
            toast.error('Failed to source candidates');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-700';
            case 'paused': return 'bg-yellow-100 text-yellow-700';
            case 'completed': return 'bg-blue-100 text-blue-700';
            case 'draft': return 'bg-gray-100 text-gray-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">Campaigns</h2>
                    <div className="flex gap-2">
                        {['all', 'active', 'paused', 'draft'].map(status => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    filter === status
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <button
                    onClick={onCreateCampaign}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Campaign
                </button>
            </div>

            {/* Campaign List */}
            {isLoading ? (
                <div className="text-center py-12 text-gray-500">Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Create your first autopilot campaign to automate candidate outreach
                    </p>
                    <button
                        onClick={onCreateCampaign}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                    >
                        Create Campaign
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {campaigns.map(campaign => (
                        <div
                            key={campaign._id}
                            onClick={() => onSelectCampaign(campaign)}
                            className="bg-white border border-gray-200 rounded-lg p-5 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                                    <p className="text-sm text-gray-500">
                                        {campaign.targetJob?.title || 'No job selected'}
                                    </p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                                    {campaign.status}
                                </span>
                            </div>

                            {/* Metrics */}
                            <div className="grid grid-cols-4 gap-2 mb-4">
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <div className="text-lg font-semibold text-gray-900">
                                        {campaign.metrics?.candidatesSourced || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">Sourced</div>
                                </div>
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <div className="text-lg font-semibold text-blue-600">
                                        {campaign.metrics?.emailsSent || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">Emailed</div>
                                </div>
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <div className="text-lg font-semibold text-green-600">
                                        {campaign.metrics?.responsesReceived || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">Responses</div>
                                </div>
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <div className="text-lg font-semibold text-purple-600">
                                        {campaign.metrics?.interviewsScheduled || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">Interviews</div>
                                </div>
                            </div>

                            {/* Automation Settings */}
                            <div className="flex items-center gap-3 mb-4 text-xs text-gray-600">
                                <span className={`flex items-center gap-1 ${campaign.automation?.autoApprove ? 'text-green-600' : ''}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Auto-approve {campaign.automation?.autoApprove ? 'on' : 'off'}
                                </span>
                                <span className={`flex items-center gap-1 ${campaign.automation?.autoSendEmails ? 'text-green-600' : ''}`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Auto-email {campaign.automation?.autoSendEmails ? 'on' : 'off'}
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                                {campaign.status === 'draft' && (
                                    <button
                                        onClick={(e) => handleStartCampaign(campaign._id, e)}
                                        className="px-3 py-1 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700"
                                    >
                                        Start
                                    </button>
                                )}
                                {campaign.status === 'active' && (
                                    <>
                                        <button
                                            onClick={(e) => handlePauseCampaign(campaign._id, e)}
                                            className="px-3 py-1 bg-yellow-500 text-white rounded text-sm font-medium hover:bg-yellow-600"
                                        >
                                            Pause
                                        </button>
                                        <button
                                            onClick={(e) => handleSourceCandidates(campaign._id, e)}
                                            className="px-3 py-1 border border-indigo-300 text-indigo-600 rounded text-sm font-medium hover:bg-indigo-50"
                                        >
                                            Source Candidates
                                        </button>
                                    </>
                                )}
                                {campaign.status === 'paused' && (
                                    <button
                                        onClick={(e) => handleStartCampaign(campaign._id, e)}
                                        className="px-3 py-1 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700"
                                    >
                                        Resume
                                    </button>
                                )}
                                <span className="ml-auto text-xs text-gray-400">
                                    Created {new Date(campaign.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CampaignList;
